import { useEffect, useRef, useState } from 'react';
import { Copy, Download, FileText, MoreHorizontal, Trash2 } from 'lucide-react';
import { Checkbox } from '@tes/ui';
import type { TemplateStatus, TemplateSummary } from '../../lib/types';

export const STATUS_STYLE: Record<TemplateStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  published: 'bg-emerald-100 text-emerald-700',
  archived: 'bg-amber-100 text-amber-700',
};

/** Actions a single template offers, wherever it's rendered. */
export interface RowActions {
  onOpen: () => void;
  onDuplicate: () => void;
  onExport: () => void;
  onDelete: () => void;
}

export function StatusBadges({ template }: { template: TemplateSummary }) {
  return (
    <>
      <span
        className={`rounded px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[template.status]}`}
      >
        {template.status}
      </span>
      {template.requiresSignature && (
        <span className="rounded bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-700">
          signing
        </span>
      )}
    </>
  );
}

export function TemplateCard({
  template: t,
  selected,
  onToggle,
  actions,
}: {
  template: TemplateSummary;
  selected: boolean;
  onToggle: (opts: { shift: boolean }) => void;
  actions: RowActions;
}) {
  return (
    <div
      className={`relative rounded-xl border bg-white transition-shadow hover:shadow-md ${
        selected ? 'border-brand-400 ring-1 ring-brand-300' : 'border-slate-200'
      }`}
    >
      <div className="absolute left-3 top-4 z-10">
        <Checkbox checked={selected} onToggle={onToggle} label={`Select ${t.name}`} />
      </div>
      <button
        type="button"
        onClick={actions.onOpen}
        className="flex w-full flex-col items-start p-4 text-left"
      >
        {/* Indented past the absolutely-positioned checkbox. */}
        <FileText size={20} className="mb-3 ml-7 text-brand-600" />
        <div className="pr-8 font-medium">{t.name}</div>
        <div className="mt-0.5 line-clamp-1 text-xs text-slate-400">{t.description || t.id}</div>
        <div className="mt-3 flex items-center gap-2">
          <StatusBadges template={t} />
        </div>
      </button>
      <div className="absolute right-2 top-2">
        <CardMenu templateName={t.name} actions={actions} />
      </div>
    </div>
  );
}

/**
 * Per-card ⋯ menu. A sibling of the card's navigate button rather than a child —
 * nesting a button inside a button is invalid, and as a later sibling it paints
 * on top and takes the click without opening the editor.
 */
function CardMenu({ templateName, actions }: { templateName: string; actions: RowActions }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const item = (
    onClick: () => void,
    Icon: typeof Copy,
    text: string,
    tone = 'text-slate-700 hover:bg-slate-50',
  ) => (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        setOpen(false);
        onClick();
      }}
      className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm ${tone}`}
    >
      <Icon size={14} /> {text}
    </button>
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Actions for ${templateName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-10 mt-1 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          {item(actions.onDuplicate, Copy, 'Duplicate')}
          {item(actions.onExport, Download, 'Export JSON')}
          {item(actions.onDelete, Trash2, 'Delete…', 'text-red-600 hover:bg-red-50')}
        </div>
      )}
    </div>
  );
}
